import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { isAudioMimeType, MimeType } from '@repo/shared';

import Audio from '@/components/blocks/preview/audio';
import { Spinner } from '@/components/ui/spinner';
import {
  useFileDownload,
  type UseFileDownloadProps,
} from '@/hooks/use-file-download';
import { useMount } from '@/hooks/use-mount';

interface PreviewProps {
  id: string;
  options: UseFileDownloadProps;
}

export const Preview = ({ id, options }: PreviewProps) => {
  const { t } = useTranslation();
  const { downloadFile, isPending, data } = useFileDownload(options);
  useMount(downloadFile);

  if (isPending) {
    return <Spinner />;
  }

  if (!data) {
    return <p>{t('blocks.preview.unsupported')}</p>;
  }

  if ([MimeType.PDF].includes(data.contentType)) {
    return <p>{t('blocks.preview.pdf')}</p>;
  }

  if ([MimeType.DOCX, MimeType.DOC].includes(data.contentType)) {
    return <p>{t('blocks.preview.word')}</p>;
  }

  if (isAudioMimeType(data.contentType)) {
    return <Audio id={id} />;
  }

  if (
    [
      MimeType.JPEG,
      MimeType.PNG,
      MimeType.APNG,
      MimeType.WEBP,
      MimeType.AVIF,
      MimeType.GIF,
    ].includes(data.contentType)
  ) {
    return (
      <img
        src={data.url}
        alt={data.fileName}
        className='max-h-[100px]'
      />
    );
  }

  return (
    <Suspense fallback={<Spinner />}>
      <p className='max-w-[200px] max-h-[100px] overflow-y-auto text-sm'>
        {data.text}
      </p>
    </Suspense>
  );
};
